import React, { useState, useEffect, useMemo } from 'react';
import {
  createInitialChessGame,
  getChessLegalMovesForPiece,
  applyChessMove,
  getBestChessAIMove,
  type ChessGameState,
  type ChessPiece,
  type ChessPieceType,
  type ChessColor,
  type ChessMove,
} from '@gamengayxua/shared';
import { api } from '../../api/client.ts';

interface ChessGameProps {
  onBackToHub: () => void;
}

type GameMode = 'ai' | 'pvp';
type AIDifficulty = 'easy' | 'medium';

// Authentic Unicode Piece glyphs with high visibility
const PIECE_GLYPHS: Record<ChessPieceType, { white: string; black: string; nameVi: string; value: number }> = {
  king: { white: '♔', black: '♚', nameVi: 'Vua', value: 1000 },
  queen: { white: '♕', black: '♛', nameVi: 'Hậu', value: 9 },
  rook: { white: '♖', black: '♜', nameVi: 'Xe', value: 5 },
  bishop: { white: '♗', black: '♝', nameVi: 'Tượng', value: 3 },
  knight: { white: '♘', black: '♞', nameVi: 'Mã', value: 3 },
  pawn: { white: '♙', black: '♟', nameVi: 'Tốt', value: 1 },
};

// Procedural Web Audio Sound Effects
function playChessPieceTap() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(360, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.07);
    gain.gain.setValueAtTime(0.7, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.07);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
  } catch {}
}

function playChessCaptureSnap() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(220, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.85, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.13);
  } catch {}
}

function playChessCheckChime() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(740, ctx.currentTime);
    osc.frequency.setValueAtTime(988, ctx.currentTime + 0.09);
    gain.gain.setValueAtTime(0.55, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.28);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.29);
  } catch {}
}

export const ChessGame: React.FC<ChessGameProps> = ({ onBackToHub }) => {
  const [game, setGame] = useState<ChessGameState>(createInitialChessGame());
  const [selectedPiece, setSelectedPiece] = useState<ChessPiece | null>(null);
  const [legalMoves, setLegalMoves] = useState<{ toRow: number; toCol: number }[]>([]);
  const [gameMode, setGameMode] = useState<GameMode>('ai');
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium');
  const [isAiThinking, setIsAiThinking] = useState<boolean>(false);
  const [historyStack, setHistoryStack] = useState<ChessGameState[]>([]);
  const [hintMove, setHintMove] = useState<{ fromRow: number; fromCol: number; toRow: number; toCol: number } | null>(null);

  // Pawn Promotion State
  const [pendingPromotion, setPendingPromotion] = useState<{
    fromRow: number;
    fromCol: number;
    toRow: number;
    toCol: number;
  } | null>(null);

  // Captured pieces calculation
  const capturedWhite = useMemo(() => {
    return game.moveHistory
      .filter((m) => m.captured && m.captured.color === 'white')
      .map((m) => m.captured!);
  }, [game.moveHistory]);

  const capturedBlack = useMemo(() => {
    return game.moveHistory
      .filter((m) => m.captured && m.captured.color === 'black')
      .map((m) => m.captured!);
  }, [game.moveHistory]);

  // Material evaluation advantage
  const materialAdvantage = useMemo(() => {
    let whiteTotal = 0;
    let blackTotal = 0;
    for (const p of game.pieces) {
      const val = PIECE_GLYPHS[p.type].value;
      if (val < 500) {
        if (p.color === 'white') whiteTotal += val;
        else blackTotal += val;
      }
    }
    return whiteTotal - blackTotal;
  }, [game.pieces]);

  // AI Turn Trigger
  useEffect(() => {
    if (gameMode === 'ai' && game.turn === 'black' && !game.winner && !isAiThinking) {
      setIsAiThinking(true);
      const timer = setTimeout(() => {
        const depth = aiDifficulty === 'easy' ? 1 : 2;
        const aiMove = getBestChessAIMove(game, depth);
        if (aiMove) {
          setHistoryStack((prev) => [...prev, game]);
          const nextState = applyChessMove(game, aiMove.fromRow, aiMove.fromCol, aiMove.toRow, aiMove.toCol, 'queen');
          setGame(nextState);
          setSelectedPiece(null);
          setLegalMoves([]);
          setHintMove(null);

          const isCap = !!game.pieces.find((p) => p.row === aiMove.toRow && p.col === aiMove.toCol);
          if (isCap) playChessCaptureSnap();
          else playChessPieceTap();

          if (nextState.isCheck) playChessCheckChime();

          if (nextState.winner) {
            api.saveGameProgress('chess', { winner: nextState.winner }, `Ván Cờ Vua: ${nextState.winner === 'white' ? 'Trắng thắng' : nextState.winner === 'black' ? 'Đen thắng' : 'Hòa'}`).catch(() => {});
          }
        }
        setIsAiThinking(false);
      }, 450);

      return () => clearTimeout(timer);
    }
  }, [game, gameMode, aiDifficulty, isAiThinking]);

  // Square Click Handler
  const handleSquareClick = (row: number, col: number) => {
    if (game.winner || (gameMode === 'ai' && game.turn === 'black') || isAiThinking) return;

    const clickedPiece = game.pieces.find((p) => p.row === row && p.col === col);

    // If clicking own piece, select it and compute legal moves
    if (clickedPiece && clickedPiece.color === game.turn) {
      setSelectedPiece(clickedPiece);
      const moves = getChessLegalMovesForPiece(clickedPiece, game.pieces, game.enPassantTarget);
      setLegalMoves(moves);
      setHintMove(null);
      return;
    }

    // If a piece is selected and clicking a valid destination
    if (selectedPiece) {
      const isDestinationValid = legalMoves.some((m) => m.toRow === row && m.toCol === col);
      if (isDestinationValid) {
        // Check if pawn promotion
        if (selectedPiece.type === 'pawn' && (row === 0 || row === 7)) {
          setPendingPromotion({
            fromRow: selectedPiece.row,
            fromCol: selectedPiece.col,
            toRow: row,
            toCol: col,
          });
          return;
        }

        executeMove(selectedPiece.row, selectedPiece.col, row, col, 'queen');
        return;
      }
    }

    // Deselect if clicking empty invalid square
    setSelectedPiece(null);
    setLegalMoves([]);
  };

  const executeMove = (
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    promoteTo: ChessPieceType = 'queen'
  ) => {
    const isCap = !!game.pieces.find((p) => p.row === toRow && p.col === toCol);
    setHistoryStack((prev) => [...prev, game]);
    const nextState = applyChessMove(game, fromRow, fromCol, toRow, toCol, promoteTo);
    setGame(nextState);
    setSelectedPiece(null);
    setLegalMoves([]);
    setHintMove(null);
    setPendingPromotion(null);

    if (isCap) playChessCaptureSnap();
    else playChessPieceTap();

    if (nextState.isCheck) playChessCheckChime();

    if (nextState.winner) {
      api.saveGameProgress('chess', { winner: nextState.winner }, `Ván Cờ Vua: ${nextState.winner === 'white' ? 'Trắng thắng' : nextState.winner === 'black' ? 'Đen thắng' : 'Hòa'}`).catch(() => {});
    }
  };

  // Undo Move
  const handleUndo = () => {
    if (historyStack.length === 0 || isAiThinking) return;

    if (gameMode === 'ai') {
      if (historyStack.length >= 2) {
        const targetState = historyStack[historyStack.length - 2];
        setHistoryStack((prev) => prev.slice(0, prev.length - 2));
        setGame(targetState);
      } else {
        const targetState = historyStack[0];
        setHistoryStack([]);
        setGame(targetState);
      }
    } else {
      const targetState = historyStack[historyStack.length - 1];
      setHistoryStack((prev) => prev.slice(0, prev.length - 1));
      setGame(targetState);
    }

    setSelectedPiece(null);
    setLegalMoves([]);
    setHintMove(null);
    setPendingPromotion(null);
  };

  // Reset Game
  const handleNewGame = () => {
    setGame(createInitialChessGame());
    setSelectedPiece(null);
    setLegalMoves([]);
    setHistoryStack([]);
    setHintMove(null);
    setPendingPromotion(null);
  };

  // Hint Move
  const handleHint = () => {
    if (game.winner || isAiThinking) return;
    const best = getBestChessAIMove(game, 2);
    if (best) {
      setHintMove(best);
      const piece = game.pieces.find((p) => p.row === best.fromRow && p.col === best.fromCol);
      if (piece) {
        setSelectedPiece(piece);
        setLegalMoves(getChessLegalMovesForPiece(piece, game.pieces, game.enPassantTarget));
      }
    }
  };

  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

  return (
    <div className="max-w-md mx-auto p-3 space-y-3 select-none" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-3.5 py-2 text-sm font-bold bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span className="text-base">←</span>
          <span>GameHub</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-base font-black text-cyan-400 tracking-wide flex items-center space-x-1.5">
            <span>♚</span>
            <span>CỜ VUA TRÍ TUỆ</span>
          </span>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-bold border border-cyan-500/30">
            CHESS ENGINE
          </span>
        </div>
      </div>

      {/* Control Bar: Mode + Difficulty */}
      <div className="bg-slate-900/90 p-2 rounded-xl border border-slate-800 flex items-center justify-between text-xs sm:text-sm">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => { setGameMode('ai'); handleNewGame(); }}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
              gameMode === 'ai'
                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-md'
                : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            🤖 Đấu với AI
          </button>
          <button
            onClick={() => { setGameMode('pvp'); handleNewGame(); }}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
              gameMode === 'pvp'
                ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-md'
                : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            👥 2 Người
          </button>
        </div>

        {gameMode === 'ai' && (
          <div className="flex items-center space-x-1">
            <span className="text-slate-400 text-xs">Cấp:</span>
            <button
              onClick={() => setAiDifficulty(aiDifficulty === 'easy' ? 'medium' : 'easy')}
              className="px-2.5 py-1 rounded bg-slate-800 border border-slate-700 text-cyan-300 font-bold text-xs"
            >
              {aiDifficulty === 'easy' ? 'Tập sự' : 'Kỳ thủ'}
            </button>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="bg-slate-900/80 px-3 py-2 rounded-xl border border-slate-800 flex items-center justify-between text-sm">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-slate-400 font-semibold">Lượt đi:</span>
          <div className="flex items-center space-x-1.5">
            <span
              className={`w-3.5 h-3.5 rounded-full inline-block border ${
                game.turn === 'white' ? 'bg-amber-100 border-amber-300 shadow-sm' : 'bg-slate-800 border-slate-600'
              }`}
            />
            <span className={`font-black text-sm ${game.turn === 'white' ? 'text-amber-200' : 'text-slate-300'}`}>
              {game.turn === 'white' ? 'Quân Trắng' : 'Quân Đen'}
            </span>
            {isAiThinking && (
              <span className="text-xs text-cyan-400 animate-pulse font-semibold ml-1">
                (AI đang tính...)
              </span>
            )}
          </div>
        </div>

        {game.isCheck && !game.winner && (
          <div className="flex items-center space-x-1 px-2.5 py-1 bg-rose-500/20 border border-rose-500/40 rounded-full animate-bounce">
            <span className="text-rose-400 text-xs font-black">⚠️ ĐANG BỊ CHIẾU!</span>
          </div>
        )}

        {game.winner && (
          <div className="px-2.5 py-1 bg-amber-500/20 border border-amber-500/40 rounded-full">
            <span className="text-amber-300 text-xs font-black">
              {game.winner === 'draw' ? 'HÒA CỜ' : `👑 ${game.winner === 'white' ? 'TRẮNG' : 'ĐEN'} THẮNG`}
            </span>
          </div>
        )}
      </div>

      {/* Top Captured Bar (Black side - captured White pieces) */}
      <div className="bg-slate-950/60 px-3 py-1.5 rounded-lg border border-slate-800/80 flex items-center justify-between min-h-[36px]">
        <div className="flex items-center space-x-1 overflow-x-auto text-base">
          <span className="text-xs text-slate-400 mr-1">♚ Đen:</span>
          {capturedWhite.length === 0 ? (
            <span className="text-slate-600 text-xs italic">Chưa ăn quân</span>
          ) : (
            capturedWhite.map((p, idx) => (
              <span key={`cap_w_${idx}`} className="text-amber-200 drop-shadow">
                {PIECE_GLYPHS[p.type].white}
              </span>
            ))
          )}
        </div>
        {materialAdvantage < 0 && (
          <span className="text-xs font-black text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
            +{Math.abs(materialAdvantage)}
          </span>
        )}
      </div>

      {/* 8x8 Chess Board Container */}
      <div className="retro-card p-2 sm:p-3 bg-slate-900 border-slate-700/80 rounded-2xl shadow-2xl relative">
        <div className="w-full aspect-square max-w-[420px] mx-auto grid grid-cols-8 grid-rows-8 border-4 border-amber-950/60 rounded-xl overflow-hidden shadow-2xl select-none relative">
          {Array.from({ length: 8 }).map((_, row) =>
            Array.from({ length: 8 }).map((_, col) => {
              const isDark = (row + col) % 2 === 1;
              const piece = game.pieces.find((p) => p.row === row && p.col === col);
              const isSelected = selectedPiece?.row === row && selectedPiece?.col === col;
              const isLegalTarget = legalMoves.some((m) => m.toRow === row && m.toCol === col);
              const isHintSource = hintMove?.fromRow === row && hintMove?.fromCol === col;
              const isHintDest = hintMove?.toRow === row && hintMove?.toCol === col;
              const isLastMove = game.lastMove && (
                (game.lastMove.fromRow === row && game.lastMove.fromCol === col) ||
                (game.lastMove.toRow === row && game.lastMove.toCol === col)
              );
              const isKingInCheckSquare = piece?.type === 'king' && piece?.color === game.turn && game.isCheck;

              return (
                <button
                  key={`sq_${row}_${col}`}
                  onClick={() => handleSquareClick(row, col)}
                  aria-label={`Square ${files[col]}${8 - row}`}
                  className={`relative flex items-center justify-center transition-all ${
                    isDark ? 'bg-[#769656]' : 'bg-[#eeeed2]'
                  } ${isLastMove ? 'after:absolute after:inset-0 after:bg-yellow-400/25' : ''} ${
                    isSelected ? 'ring-4 ring-cyan-400 z-10' : ''
                  } ${isHintSource || isHintDest ? 'ring-2 ring-emerald-400' : ''} ${
                    isKingInCheckSquare ? 'ring-4 ring-rose-500 after:absolute after:inset-0 after:bg-rose-500/40 z-10 animate-pulse' : ''
                  }`}
                >
                  {/* Rank coordinate label (on file a) */}
                  {col === 0 && (
                    <span
                      className={`absolute top-0.5 left-1 text-[9px] font-black pointer-events-none select-none ${
                        isDark ? 'text-[#eeeed2]/80' : 'text-[#769656]/90'
                      }`}
                    >
                      {8 - row}
                    </span>
                  )}

                  {/* File coordinate label (on rank 1 / row 7) */}
                  {row === 7 && (
                    <span
                      className={`absolute bottom-0.5 right-1 text-[9px] font-black pointer-events-none select-none ${
                        isDark ? 'text-[#eeeed2]/80' : 'text-[#769656]/90'
                      }`}
                    >
                      {files[col]}
                    </span>
                  )}

                  {/* Chess Piece Display */}
                  {piece && (
                    <span
                      className={`text-3xl sm:text-4xl leading-none select-none transition-transform ${
                        piece.color === 'white'
                          ? 'text-white filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]'
                          : 'text-slate-950 filter drop-shadow-[0_1px_2px_rgba(255,255,255,0.4)]'
                      } ${isSelected ? 'scale-115' : 'active:scale-95'}`}
                    >
                      {PIECE_GLYPHS[piece.type][piece.color]}
                    </span>
                  )}

                  {/* Legal Destination Highlight */}
                  {isLegalTarget && !piece && (
                    <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-slate-900/40 shadow-inner pointer-events-none" />
                  )}

                  {isLegalTarget && piece && (
                    <div className="absolute inset-0 rounded-none border-4 border-rose-500/70 bg-rose-500/20 pointer-events-none" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Bottom Captured Bar (White side - captured Black pieces) */}
      <div className="bg-slate-950/60 px-3 py-1.5 rounded-lg border border-slate-800/80 flex items-center justify-between min-h-[36px]">
        <div className="flex items-center space-x-1 overflow-x-auto text-base">
          <span className="text-xs text-slate-400 mr-1">♔ Trắng:</span>
          {capturedBlack.length === 0 ? (
            <span className="text-slate-600 text-xs italic">Chưa ăn quân</span>
          ) : (
            capturedBlack.map((p, idx) => (
              <span key={`cap_b_${idx}`} className="text-slate-300 drop-shadow">
                {PIECE_GLYPHS[p.type].black}
              </span>
            ))
          )}
        </div>
        {materialAdvantage > 0 && (
          <span className="text-xs font-black text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
            +{materialAdvantage}
          </span>
        )}
      </div>

      {/* Action Buttons: Undo, Hint, New Game */}
      <div className="grid grid-cols-3 gap-2 pt-1">
        <button
          onClick={handleUndo}
          disabled={historyStack.length === 0 || isAiThinking}
          className="py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-slate-800 text-slate-200 font-bold text-sm flex items-center justify-center space-x-1.5 border border-slate-700 active:scale-95 transition-all"
        >
          <span>↩️</span>
          <span>Đi lại</span>
        </button>

        <button
          onClick={handleHint}
          disabled={!!game.winner || isAiThinking}
          className="py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-slate-800 text-amber-300 font-bold text-sm flex items-center justify-center space-x-1.5 border border-slate-700 active:scale-95 transition-all"
        >
          <span>💡</span>
          <span>Gợi ý</span>
        </button>

        <button
          onClick={handleNewGame}
          disabled={isAiThinking}
          className="py-2.5 px-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-sm flex items-center justify-center space-x-1.5 shadow-md active:scale-95 transition-all"
        >
          <span>🔄</span>
          <span>Ván mới</span>
        </button>
      </div>

      {/* Move History Strip */}
      <div className="bg-slate-900/60 p-2.5 rounded-xl border border-slate-800">
        <div className="text-xs text-slate-400 font-bold mb-1.5 flex items-center justify-between">
          <span>📜 Biên bản ván đấu:</span>
          <span className="text-[11px] text-slate-500">{game.moveHistory.length} nước</span>
        </div>
        <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto pr-1">
          {game.moveHistory.length === 0 ? (
            <span className="text-xs text-slate-500 italic">Trận đấu chưa bắt đầu. Quân Trắng đi trước.</span>
          ) : (
            game.moveHistory.map((m, idx) => {
              const moveNum = Math.floor(idx / 2) + 1;
              const isWhiteMove = idx % 2 === 0;
              return (
                <span
                  key={`hist_${idx}`}
                  className={`text-xs px-2 py-0.5 rounded font-mono font-bold ${
                    isWhiteMove ? 'bg-slate-800 text-amber-200' : 'bg-slate-800/60 text-slate-300'
                  }`}
                >
                  {isWhiteMove ? `${moveNum}.` : ''} {m.notation}
                </span>
              );
            })
          )}
        </div>
      </div>

      {/* Pawn Promotion Modal Sheet */}
      {pendingPromotion && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-cyan-500/40 rounded-2xl p-5 max-w-xs w-full text-center space-y-4 shadow-2xl">
            <h3 className="text-lg font-black text-cyan-300">👑 PHONG CẤP TỐT</h3>
            <p className="text-xs text-slate-300">Chọn quân cờ bạn muốn nâng cấp:</p>

            <div className="grid grid-cols-4 gap-2">
              {(['queen', 'rook', 'bishop', 'knight'] as ChessPieceType[]).map((type) => (
                <button
                  key={`promo_${type}`}
                  onClick={() =>
                    executeMove(
                      pendingPromotion.fromRow,
                      pendingPromotion.fromCol,
                      pendingPromotion.toRow,
                      pendingPromotion.toCol,
                      type
                    )
                  }
                  className="p-3 bg-slate-800 hover:bg-cyan-600/30 border border-slate-700 hover:border-cyan-400 rounded-xl flex flex-col items-center space-y-1 transition-all active:scale-95"
                >
                  <span className="text-3xl text-amber-200">{PIECE_GLYPHS[type].white}</span>
                  <span className="text-[11px] font-bold text-slate-300">{PIECE_GLYPHS[type].nameVi}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Game Over Modal Banner */}
      {game.winner && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/20 via-cyan-500/20 to-blue-500/20 border border-amber-500/40 text-center space-y-2">
          <div className="text-2xl">
            {game.winner === 'white' ? '🏆 TRẮNG THẮNG CUỘC!' : game.winner === 'black' ? '🏆 ĐEN THẮNG CUỘC!' : '🤝 HÒA CỜ!'}
          </div>
          <p className="text-xs text-slate-200">
            {game.isCheckmate
              ? 'Chiếu bí thành công! Ván cờ đã kết thúc một cách oanh liệt.'
              : game.isStalemate
              ? 'Thế cờ Pat (hết nước đi hợp lệ) - kết quả hòa thỏa đáng.'
              : 'Trận đấu khép lại.'}
          </p>
          <div className="pt-2 flex justify-center space-x-3">
            <button
              onClick={handleNewGame}
              className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm shadow-lg active:scale-95"
            >
              Chơi ván mới
            </button>
            <button
              onClick={onBackToHub}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-sm active:scale-95"
            >
              Về GameHub
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
